import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { CrudPage, type FormField } from '@/components/CrudPage';
import { Badge, Button, Card, Toast } from '@/components/ui';
import { formatRelative } from '@/lib/format';
import { dialerIndex, dialerLabel, dialerOptions, useDialers } from '@/lib/dialers';
import { BulkVicidialUsers } from '@/components/BulkVicidialUsers';
import { AssignBotToAgents } from '@/components/AssignBotToAgents';
import {
  AgentLoginResultsModal,
  type AgentLoginResult,
} from '@/components/AgentLoginResults';

interface VicidialUser {
  id: string;
  vicidial_user: string;
  name: string;
  email: string | null;
  campaign: string | null;
  in_group: string | null;
  enabled: boolean;
  assigned_bot_id: string | null;
  integration_id: string | null;
  last_seen_at: string | null;
  login_state: string;
}

interface AgentTestResult {
  agents: AgentLoginResult[];
  agents_total: number;
  agents_ok: number;
  ok: boolean;
  errors: string[];
}

interface Bot {
  id: string;
  name: string;
  code: string;
  kind: string;
}

export default function VicidialUsers() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);
  const [agentTest, setAgentTest] = useState<AgentTestResult | null>(null);

  const { data: bots } = useQuery({
    queryKey: ['bots-assignable'],
    // NOT `/bots`, which is scoped to AI Bots. A Soundboard Bot is assigned
    // to a Vicidial user exactly like an AI Bot is, and this list was the
    // only place to do it — so the product could be built and switched on
    // and never reached a call.
    queryFn: () => api.get<{ data: Bot[] }>('/bot-assignments/assignable'),
  });

  const botsById = new Map((bots?.data ?? []).map((bot) => [bot.id, bot]));

  const { data: dialers } = useDialers();
  const dialersById = dialerIndex(dialers?.data);
  // Empty for a client with one server: a filter offering a single choice, and
  // a required field with one option, are both just noise.
  const servers = dialerOptions(dialers?.data);
  const multiServer = servers.length > 0;

  // The same check Test sync runs per server, run from the list the operator is
  // actually looking at. An agent row existing here says nothing about whether
  // Vicidial will accept it, and a Remote Agent that cannot log in produces
  // silence on the call rather than an error anybody sees.
  const testAgents = useMutation({
    mutationFn: () => api.post<AgentTestResult>('/vicidial-users/test-logins', {}),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-users'] });
      setAgentTest(result);
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not test the agents'), tone: 'bad' }),
  });

  const sync = useMutation({
    mutationFn: () =>
      api.post<{ users_imported: number; campaigns_imported: number; mock_mode: boolean; errors: string[] }>(
        '/vicidial/sync',
        { import_users: true, import_campaigns: true },
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-users'] });
      const suffix = result.mock_mode ? ' (mock mode)' : '';
      setToast({
        message: result.errors.length
          ? `Imported ${result.users_imported} users with warnings: ${result.errors.join('; ')}`
          : `Imported ${result.users_imported} users and ${result.campaigns_imported} campaigns${suffix}`,
        tone: result.errors.length ? 'bad' : 'good',
      });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Import failed'),
        tone: 'bad',
      }),
  });

  const fields: FormField[] = [
    ...(multiServer
      ? [
          {
            name: 'integration_id',
            label: 'Vicidial server',
            type: 'select' as const,
            required: true,
            options: servers,
            hint: 'The dialer this agent logs into. Usernames repeat across servers.',
          },
        ]
      : []),
    {
      name: 'vicidial_user',
      label: 'Vicidial username',
      required: true,
      immutable: true,
      placeholder: 'agent001',
      hint: 'Must match the username in Vicidial exactly.',
    },
    { name: 'name', label: 'Full name', required: true, placeholder: 'Agent 01' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'campaign', label: 'Campaign', placeholder: 'HOME01' },
    { name: 'in_group', label: 'In-group', placeholder: 'VERIFIER01' },
    {
      name: 'assigned_bot_id',
      label: 'Primary bot',
      type: 'select',
      options: (bots?.data ?? []).map((bot) => ({
        value: bot.id,
        label:
          bot.kind === 'SOUNDBOARD'
            ? `${bot.name} (${bot.code}) — soundboard`
            : `${bot.name} (${bot.code})`,
      })),
      hint: 'The simple one-user-one-bot pointer. Assignments give finer control.',
    },
    {
      name: 'vicidial_password',
      label: 'Vicidial password',
      type: 'password',
      hint: 'Optional. Stored encrypted and never shown again.',
    },
  ];

  return (
    <>
      <CrudPage<VicidialUser>
        title="Vicidial users"
        description="The dialer agents whose answered calls are handed to a bot. Use Test sync on the Vicidial servers page to check they can actually log in."
        resource="vicidial_users"
        path="/vicidial-users"
        queryKey="vicidial-users"
        searchPlaceholder="Search users…"
        emptyTitle="No Vicidial users yet"
        emptyDescription="Import them from your dialer, or add them one at a time."
        createLabel="New Vicidial user"
        extraActions={
          <>
            {can('settings:read') && (
              <Button
                variant="secondary"
                loading={testAgents.isPending}
                onClick={() => testAgents.mutate()}
              >
                Test agents
              </Button>
            )}
            {can('vicidial_users:update') && <AssignBotToAgents bots={bots?.data ?? []} />}
            {can('vicidial_users:create') && (
              <>
                <BulkVicidialUsers />
                <Button
                  variant="secondary"
                  loading={sync.isPending}
                  onClick={() => sync.mutate()}
                >
                  Import from Vicidial
                </Button>
              </>
            )}
          </>
        }
        filters={[
          {
            key: 'enabled',
            label: 'Status',
            options: [
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' },
            ],
          },
          ...(multiServer
            ? [{ key: 'integration_id', label: 'Vicidial server', options: servers }]
            : []),
        ]}
        fields={fields}
        columns={[
          {
            key: 'user',
            header: 'User',
            render: (user) => (
              <div>
                <div className="font-mono text-sm text-ink-900 dark:text-ink-50">
                  {user.vicidial_user}
                </div>
                <div className="text-xs text-ink-500">{user.name}</div>
              </div>
            ),
          },
          ...(multiServer
            ? [
                {
                  key: 'server',
                  header: 'Vicidial server',
                  render: (user: VicidialUser) =>
                    user.integration_id ? (
                      <Badge tone="blue">
                        {dialerLabel(dialersById.get(user.integration_id))}
                      </Badge>
                    ) : (
                      <Badge tone="amber">No server</Badge>
                    ),
                },
              ]
            : []),
          {
            key: 'campaign',
            header: 'Campaign / in-group',
            render: (user) => (
              <span className="text-xs text-ink-600 dark:text-ink-300">
                {user.campaign ?? '—'}
                {user.in_group && ` / ${user.in_group}`}
              </span>
            ),
          },
          {
            key: 'bot',
            header: 'Primary bot',
            render: (user) =>
              user.assigned_bot_id ? (
                <Badge tone="blue">
                  {botsById.get(user.assigned_bot_id)?.name ?? 'Assigned'}
                </Badge>
              ) : (
                <Badge tone="amber">Unassigned</Badge>
              ),
          },
          {
            key: 'login',
            header: 'Can log in',
            render: (user) =>
              user.login_state === 'OK' ? (
                <Badge tone="green">Yes</Badge>
              ) : user.login_state === 'NO_PASSWORD' ? (
                <Badge tone="amber">No password</Badge>
              ) : user.login_state === 'FAILED' ? (
                <Badge tone="red">Rejected</Badge>
              ) : (
                <Badge tone="gray">Unchecked</Badge>
              ),
          },
          {
            key: 'enabled',
            header: 'Status',
            render: (user) => (
              <Badge tone={user.enabled ? 'green' : 'gray'}>
                {user.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            ),
          },
          {
            key: 'last_seen',
            header: 'Last seen',
            render: (user) => (
              <span className="text-xs text-ink-500">{formatRelative(user.last_seen_at)}</span>
            ),
          },
        ]}
      >
        <Card className="mb-4">
          <p className="text-sm text-ink-600 dark:text-ink-300">
            Vicidial passwords are never stored in plaintext — they are encrypted at rest and
            never returned by the API. A user here only needs a password if your integration
            requires per-agent credentials.
          </p>
        </Card>
      </CrudPage>

      {agentTest && (
        <AgentLoginResultsModal result={agentTest} onClose={() => setAgentTest(null)} />
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
